import { Box, Flex, Slider, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useCallback, useState } from 'react';
import type { FC } from 'react';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';

interface ImageCropperProps {
  imageSrc: string;
  initialCrop?: { x: number; y: number } | null;
  initialZoom?: number | null;
  onCropComplete: (croppedAreaPixels: Area) => void;
  onCropChange?: (crop: { x: number; y: number }, zoom: number) => void;
}

export const ImageCropper: FC<ImageCropperProps> = ({
  imageSrc,
  initialCrop,
  initialZoom,
  onCropComplete,
  onCropChange,
}) => {
  const [crop, setCrop] = useState(initialCrop ?? { x: 0, y: 0 });
  const [zoom, setZoom] = useState(initialZoom ?? 1);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const handleCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      onCropComplete(croppedAreaPixels);
    },
    [onCropComplete],
  );

  return (
    <Flex direction="column" h="100%" style={{ flex: 1 }}>
      <Box pos="relative" style={{ flex: 1, minHeight: 300 }}>
        <Cropper
          showGrid
          aria-label="Crop your image. Use arrow keys to reposition."
          aspect={4 / 5}
          crop={crop}
          cropShape="rect"
          image={imageSrc}
          mediaProps={{ crossOrigin: 'anonymous' }}
          zoom={zoom}
          onCropChange={(c) => {
            setCrop(c);
            onCropChange?.(c, zoom);
          }}
          onCropComplete={handleCropComplete}
          onZoomChange={(z) => {
            setZoom(z);
            onCropChange?.(crop, z);
          }}
        />
      </Box>

      <Flex align="center" direction="column" gap={4} p="sm">
        <Flex align="center" gap="sm" maw={300} w="100%">
          <Text c="dimmed" size="xs">
            −
          </Text>
          <Slider
            aria-label="Zoom"
            color="gray.5"
            label={null}
            max={3}
            min={1}
            step={0.1}
            style={{ flex: 1 }}
            styles={{
              track: { backgroundColor: 'rgba(255,255,255,0.15)' },
              thumb: { backgroundColor: 'white', borderColor: 'white' },
              bar: { backgroundColor: 'rgba(255,255,255,0.5)' },
            }}
            value={zoom}
            onChange={setZoom}
          />
          <Text c="dimmed" size="xs">
            +
          </Text>
        </Flex>
        <Text c="dimmed" size="xs">
          {isMobile ? 'Drag to reposition · Pinch to zoom' : 'Drag to reposition · Scroll to zoom'}
        </Text>
      </Flex>
    </Flex>
  );
};
